if (!customElements.get('gift-guide-tabbed-carousel')) {
  class GiftGuideTabbedCarousel extends HTMLElement {
    connectedCallback() {
      this.delayedFired = false;
      this.pendingPanels = new Set();
      this.resultId = null;

      this.bindTabClicks();

      const firstPanel = this.querySelector('.gift-guide-tabbed-carousel__panel.is-active');
      if (firstPanel) {
        this.pendingPanels.add(firstPanel);
      }

      window.addEventListener('Delayed', () => {
        this.delayedFired = true;
        this.pendingPanels.forEach(panel => this.fetchAndRender(panel));
        this.pendingPanels.clear();
      }, { once: true });
    }

    bindTabClicks() {
      this.querySelectorAll('.gift-guide-tabbed-carousel__tab').forEach(btn => {
        btn.addEventListener('click', () => {
          this.activateTab(parseInt(btn.dataset.tabIndex, 10));
        });
      });
    }

    activateTab(index) {
      this.querySelectorAll('.gift-guide-tabbed-carousel__tab').forEach(btn => {
        const active = parseInt(btn.dataset.tabIndex, 10) === index;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', active ? 'true' : 'false');

        if (active) {
          const ctaEl = this.querySelector('[data-tab-row-cta]');
          if (ctaEl && btn.dataset.collectionUrl) {
            ctaEl.href = btn.dataset.collectionUrl;
            ctaEl.classList.add('gift-guide-tabbed-carousel__tab-row-cta--visible');
          } else if (ctaEl && !btn.dataset.collectionUrl) {
            ctaEl.classList.remove('gift-guide-tabbed-carousel__tab-row-cta--visible');
          }
        }
      });

      this.querySelectorAll('.gift-guide-tabbed-carousel__panel').forEach(panel => {
        const active = parseInt(panel.dataset.tabIndex, 10) === index;
        panel.classList.toggle('is-active', active);

        if (active && !panel.dataset.loaded) {
          if (this.delayedFired) {
            this.fetchAndRender(panel);
          } else {
            this.pendingPanels.add(panel);
          }
        }
      });
    }

    revealCarousel(panel) {
      const loading = panel.querySelector('.gift-guide-tabbed-carousel__loading');
      if (loading) loading.remove();
      const splideEl = panel.querySelector('.splide');
      if (splideEl) splideEl.style.visibility = 'visible';
    }

    buildQuery(panel) {
      const numResults = 8;
      const baseFilters = { StockStatus: 'In Stock' };
      const andClauses = [];

      const slots = [
        {
          filterBy: panel.dataset.filterBy1,
          occasionValue: panel.dataset.occasionValue1,
          subthemeValue: panel.dataset.subthemeValue1,
          recipientValue: panel.dataset.recipientValue1,
          priceOperator: panel.dataset.priceOperator1,
          priceValue: panel.dataset.priceValue1,
          priceValueMax: panel.dataset.priceValueMax1,
        },
        {
          filterBy: panel.dataset.filterBy2,
          occasionValue: panel.dataset.occasionValue2,
          subthemeValue: panel.dataset.subthemeValue2,
          recipientValue: panel.dataset.recipientValue2,
          priceOperator: panel.dataset.priceOperator2,
          priceValue: panel.dataset.priceValue2,
          priceValueMax: panel.dataset.priceValueMax2,
        },
        {
          filterBy: panel.dataset.filterBy3,
          occasionValue: panel.dataset.occasionValue3,
          subthemeValue: panel.dataset.subthemeValue3,
          recipientValue: panel.dataset.recipientValue3,
          priceOperator: panel.dataset.priceOperator3,
          priceValue: panel.dataset.priceValue3,
          priceValueMax: panel.dataset.priceValueMax3,
        },
      ];

      for (const slot of slots) {
        if (!slot.filterBy || slot.filterBy === 'none') continue;

        if (slot.filterBy === 'occasion' && slot.occasionValue) {
          const values = slot.occasionValue.split(',').map(v => v.trim()).filter(Boolean);
          if (values.length) andClauses.push({ or: values.map(v => ({ name: 'Occasion', value: v })) });
        }

        if (slot.filterBy === 'subtheme' && slot.subthemeValue) {
          const values = slot.subthemeValue.split(',').map(v => v.trim()).filter(Boolean);
          if (values.length) andClauses.push({ or: values.map(v => ({ name: 'SubTheme', value: v })) });
        }

        if (slot.filterBy === 'recipient' && slot.recipientValue) {
          const values = slot.recipientValue.split(',').map(v => v.trim()).filter(Boolean);
          if (values.length) andClauses.push({ or: values.map(v => ({ name: 'RecipientFilter', value: v })) });
        }

        if (slot.filterBy === 'price' && slot.priceValue) {
          const op = slot.priceOperator;
          const val = parseFloat(slot.priceValue);
          let range;
          if (op === 'Between') {
            range = [val, parseFloat(slot.priceValueMax)];
          } else if (op === 'Under') {
            range = [1, val - 0.01];
          } else {
            range = [val + 0.01, 1000];
          }
          andClauses.push({ name: 'Price', range });
        }
      }

      // Fallback: all slots are none
      if (!andClauses.length) {
        andClauses.push({ name: 'Occasion', value: 'Birthday' });
      }

      return {
        numResults,
        filters: baseFilters,
        preFilterExpression: { and: andClauses },
      };
    }

    escAttr(val) {
      return String(val == null ? '' : val)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;');
    }

    buildApiUrl(podId, query) {
      const apiKey = window._accnstrc || '';
      const url = new URL(`https://ac.cnstrc.com/recommendations/v1/pods/${encodeURIComponent(podId)}`);
      url.searchParams.set('key', apiKey);
      url.searchParams.set('num_results', String(query.numResults || 8));
      url.searchParams.set('c', 'ciojs-client-2.49.0');

      if (query.filters) {
        Object.entries(query.filters).forEach(([field, value]) => {
          url.searchParams.append(`filters[${field}]`, String(value));
        });
      }

      if (query.preFilterExpression) {
        url.searchParams.set('pre_filter_expression', JSON.stringify(query.preFilterExpression));
      }

      return url.toString();
    }

    async fetchAndRender(panel) {
      const podId = panel.dataset.podId;
      if (!podId) {
        panel.dataset.loaded = 'true';
        return;
      }

      const query = this.buildQuery(panel);
      const apiUrl = this.buildApiUrl(podId, query);

      let data;
      try {
        const response = await fetch(apiUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        data = await response.json();
        this.resultId = data?.result_id;
      } catch (err) {
        console.error('[gift-guide-tabbed-carousel] Fetch failed', err);
        this.revealCarousel(panel);
        return;
      }

      let results = data?.response;
      if (!results?.results?.length) {
        const fallbackQuery = {
          numResults: 8,
          filters: { StockStatus: 'In Stock' },
          preFilterExpression: { and: [{ name: 'Price', range: [0, 1000] }] },
        };
        try {
          const fallbackResponse = await fetch(this.buildApiUrl(podId, fallbackQuery));
          if (!fallbackResponse.ok) throw new Error(`HTTP ${fallbackResponse.status}`);
          const fallbackData = await fallbackResponse.json();
          this.resultId = fallbackData?.result_id;
          results = fallbackData?.response;
        } catch (err) {
          console.error('[gift-guide-tabbed-carousel] Fallback fetch failed', err);
        }
      }

      if (!results?.results?.length) {
        this.revealCarousel(panel);
        return;
      }

      await customElements.whenDefined('product-card');
      this.renderCards(results.results, panel);
      panel.dataset.loaded = 'true';
      await this.initSplide(panel);
      this.revealCarousel(panel);
      this.observeViewRecommendation(panel);
    }

    observeViewRecommendation(panel) {
      if (panel.viewObserverArmed) return;
      panel.viewObserverArmed = true;

      const viewObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && window.heapGlobalNew) {
            window.heapGlobalNew.trackViewRecommendation({ Source: 'Recommendation-LP' });
            panel.viewObserverArmed = false;
            viewObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.8 });
      viewObserver.observe(panel);
    }

    renderCards(products, panel) {
      const list = panel.querySelector('.splide__list');
      const splideTrack = panel.querySelector('.splide__track');


      if (splideTrack?.dataset) {
        splideTrack.dataset.cnstrcRecommendations = 'true';
        splideTrack.dataset.cnstrcRecommendationsPodId = panel.dataset.podId;
        splideTrack.dataset.cnstrcNumResults = String(products.length);
        splideTrack.dataset.cnstrcResultId = this.resultId;
      }

      if (!list) return;

      list.innerHTML = products.map((productData, i) => {
        const product = productData.data;
        const facet = name => product.facets?.find(f => f.name === name)?.values[0] || '';
        const collectionIds = product.group_ids?.join(',') || '';
        const videoUrlsAttr = product.video_urls?.length
          ? `video_urls="${this.escAttr(product.video_urls)}"`
          : '';

        return `
          <li class="splide__slide">
            <product-card
              class="grid__item js-product-card"
              id="${this.escAttr(product.id)}"
              title="${this.escAttr(product.title)}"
              price="${this.escAttr(product.price)}"
              compare_at_price="${this.escAttr(product.compare_at_price)}"
              image_url="${this.escAttr(product.image_url)}"
              image_urls="${this.escAttr(product.image_urls)}"
              ${videoUrlsAttr}
              url="${this.escAttr(product.url)}"
              sku="${this.escAttr(product.sku)}"
              first_variant_id="${this.escAttr(product.variant_ids?.[0])}"
              tags="${this.escAttr(product.tags)}"
              collection_ids="${this.escAttr(collectionIds)}"
              product_type="${this.escAttr(facet('ProductType'))}"
              product_line="${this.escAttr(facet('ProductLine'))}"
              everyday_badge="${this.escAttr(facet('EverydayBadge'))}"
              promotional_badge="${this.escAttr(facet('PromotionalBadge'))}"
              flash_sale_badge="${this.escAttr(facet('FlashSaleBadge'))}"
              available_quantity="${this.escAttr(product.available_quantity)}"
              rating_score="${this.escAttr(product.rating_score)}"
              total_reviews="${this.escAttr(product.total_reviews)}"
              release_time="${this.escAttr(product.__cnstrc_release_time)}"
              data-order-selection="${this.escAttr(facet('OrderSelection'))}"
              data-cnstrc-item-id="${this.escAttr(product.id)}"
              data-cnstrc-item-name="${this.escAttr(product.title)}"
              data-cnstrc-item-section="Products"
              data-cnstrc-item="Recommendation"
              data-cnstrc-strategy-id="${this.escAttr(productData.strategy?.id)}"
              current_position="${i + 1}"
            ></product-card>
          </li>
        `;
      }).join('');
    }

    async initSplide(panel) {
      await ResourceCoordinator.requestVendor('Splide');
      const splideEl = panel.querySelector('.splide');
      if (!splideEl) return;
      const splide = new Splide(splideEl, {
        type: 'loop',
        perPage: 2,
        perMove: 1,
        arrows: true,
        pagination: true,
        focus: 0,
        gap: '24px',
        mediaQuery: 'min',
        breakpoints: {
          820:  { perPage: 3 },
          1180: { perPage: 4 },
        }
      });

      splide.on('mounted', () => {
        const { Arrows } = splide.Components;
        if (Arrows) {
          Object.values(Arrows.arrows).forEach(arrow => {
            arrow.addEventListener('click', () => {
              window.heapGlobalNew?.trackInteractWithRecommendation({
                Source: 'Recommendation-LP',
                Action: 'Arrow Click',
              });
            });
          });
        }
      });

      splide.on('dragged', () => {
        window.heapGlobalNew?.trackInteractWithRecommendation({
          Source: 'Recommendation-LP',
          Action: 'Swipe',
        });
      });

      splide.mount();
    }
  }

  customElements.define('gift-guide-tabbed-carousel', GiftGuideTabbedCarousel);
}
